import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from './api';

// 偏移式分页（后端 limit/offset，不返回 total）。
// 搜索 q 走 300ms 防抖并重置到第一页；year 直接下发；翻页/筛选变化自动重新拉取。
export function usePagedResource<T>(endpoint: string, pageSize = 50) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [year, setYear] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(0);
  }, [year]);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { limit: pageSize, offset: page * pageSize };
      if (debouncedQ) params.q = debouncedQ;
      if (year) params.year = year;
      const data = await api.list<T>(endpoint, params);
      if (my !== seq.current) return;
      setRows(data);
    } catch (e) {
      if (my !== seq.current) return;
      setError(e instanceof ApiError ? e.message : '数据加载失败');
      setRows([]);
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [endpoint, pageSize, page, debouncedQ, year]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasMore = rows.length === pageSize;

  return useMemo(() => ({
    rows,
    loading,
    error,
    reload: () => void load(),
    q,
    setQ,
    year,
    setYear,
    page,
    canPrev: page > 0,
    canNext: hasMore,
    prev: () => setPage((p) => Math.max(0, p - 1)),
    next: () => setPage((p) => p + 1),
    rangeLabel: rows.length === 0
      ? '无匹配记录'
      : `第 ${page * pageSize + 1}–${page * pageSize + rows.length} 条`,
  }), [rows, loading, error, load, q, year, page, hasMore, pageSize]);
}
